import {
    differenceInCalendarDays,
    endOfMonth,
    endOfWeek,
    endOfYear,
    isSameDay,
    parseISO,
    startOfDay,
    startOfMonth,
    startOfWeek,
    startOfYear,
    subDays,
    subMonths,
    subWeeks,
    subYears,
} from 'date-fns';
import { TimeRangeOption } from 'types/dashboard';
import { getIntl } from 'utils/getTranslations';

export type DateRange = { from: Date; to: Date };
export type PeriodState = 'current' | 'past' | 'future';
export type DeltaCaption = 'as-expected' | 'ahead' | 'behind' | 'target-met' | 'over-target' | 'under-target';
export type DeltaTone = 'neutral' | 'positive' | 'negative';
export type MotivationMessage = 'fresh-day' | 'fresh-start' | 'future';

const WEEK = { weekStartsOn: 1 } as const;
/** A delta closer to zero than this reads as on pace, but its real value is still shown. */
const DEAD_BAND_HOURS = 0.5;
/** The API cap: one less than the fewest weekdays any calendar year has (260), so a working day always remains. */
export const MAX_PLANNED_WORKDAYS_OFF = 259;

/** Mon–Fri days from `from` through `to`, both inclusive. */
export const countWeekdays = ({ from, to }: DateRange): number => {
    const days = differenceInCalendarDays(to, from) + 1;
    if (days <= 0) return 0;
    let count = Math.floor(days / 7) * 5;
    for (let i = 0; i < days % 7; i++) {
        const weekday = (from.getDay() + i) % 7;
        if (weekday !== 0 && weekday !== 6) count++;
    }
    return count;
};

export const weekdaysInYear = (year: number): number =>
    countWeekdays({ from: new Date(year, 0, 1), to: new Date(year, 11, 31) });

export const plannedWorkdaysPerYear = (plannedWorkdaysOff: number, year: number): number =>
    weekdaysInYear(year) - plannedWorkdaysOff;

/**
 * The stored targets when an annual target is set, with unset days off counted as 0, else null. Also null when the
 * days off leave no working day in `year`: the API cap rules that out, but a value saved under an older, higher
 * cap would divide the annual target by zero.
 */
export const usableTargets = (
    annualTargetHours: number | null | undefined,
    plannedWorkdaysOff: number | null | undefined,
    year: number
): { annualTargetHours: number; plannedWorkdaysOff: number } | null => {
    const daysOff = plannedWorkdaysOff ?? 0;
    return typeof annualTargetHours === 'number' && plannedWorkdaysPerYear(daysOff, year) >= 1
        ? { annualTargetHours, plannedWorkdaysOff: daysOff }
        : null;
};

/** Shown in place of a target value that is not set or not valid yet. */
export const NO_VALUE = '–';

/**
 * The two derived target rows: the planned workdays written out as their formula, and the daily target. Unset days
 * off count as 0; days off leaving no working day dash both rows, the same rule as {@link usableTargets}.
 */
export const describeDerivedTargets = (
    annualTargetHours: number | null | undefined,
    plannedWorkdaysOff: number | null | undefined,
    decimals: number,
    year: number
): { plannedWorkdaysPerYear: string; dailyTarget: string } => {
    const weekdays = weekdaysInYear(year);
    const daysOff = plannedWorkdaysOff ?? 0;
    const perYear = weekdays - daysOff;
    if (!Number.isInteger(daysOff) || daysOff < 0 || perYear < 1) {
        return { plannedWorkdaysPerYear: NO_VALUE, dailyTarget: NO_VALUE };
    }
    const hasAnnualTarget =
        typeof annualTargetHours === 'number' && Number.isInteger(annualTargetHours) && annualTargetHours >= 1;
    return {
        plannedWorkdaysPerYear: `${weekdays} − ${daysOff} = ${perYear}`,
        dailyTarget: hasAnnualTarget ? formatHours(annualTargetHours / perYear, decimals) : NO_VALUE,
    };
};

/**
 * The whole period an option names, which the target math runs on — This week is Mon–Sun even
 * while it is still running. The hours are fetched for the range the dashboard already requests,
 * which ends current periods at today.
 */
export const getNominalRange = (
    option: TimeRangeOption,
    today: Date,
    customFrom?: string,
    customTo?: string
): DateRange | null => {
    const day = startOfDay(today);
    const range = (from: Date, to: Date): DateRange => ({ from: startOfDay(from), to: startOfDay(to) });
    switch (option) {
        case 'today':
            return range(day, day);
        case 'yesterday':
            return range(subDays(day, 1), subDays(day, 1));
        case 'current-week':
            return range(startOfWeek(day, WEEK), endOfWeek(day, WEEK));
        case 'last-week':
            return range(startOfWeek(subWeeks(day, 1), WEEK), endOfWeek(subWeeks(day, 1), WEEK));
        case 'current-month':
            return range(startOfMonth(day), endOfMonth(day));
        case 'last-month':
            return range(startOfMonth(subMonths(day, 1)), endOfMonth(subMonths(day, 1)));
        case 'current-year':
            return range(startOfYear(day), endOfYear(day));
        case 'last-year':
            return range(startOfYear(subYears(day, 1)), endOfYear(subYears(day, 1)));
        case 'custom':
            return customFrom && customTo ? range(parseISO(customFrom), parseISO(customTo)) : null;
    }
};

export type ProgressInput = {
    option: TimeRangeOption;
    range: DateRange;
    today: Date;
    annualTargetHours: number;
    plannedWorkdaysOff: number;
    actualHours: number;
};

export type Progress = {
    state: PeriodState;
    isFullYear: boolean;
    isSingleDay: boolean;
    dailyTarget: number;
    periodTarget: number;
    expectedHours: number;
    /** Weekdays in the period, and those of them up to and including today, which `expectedHours` is prorated over. */
    weekdays: number;
    elapsed: number;
    delta: number;
    /** Null when the period has no weekdays, so there is no target to measure against. */
    percent: number | null;
    /** Shares of the bar width, 0–1. Null hides the bar; the expected tick shows only while the period runs. */
    bar: { fill: number; targetTick: number; expectedTick: number | null } | null;
    projectedTotal: number | null;
    /** Set when the footer shows the delta; `message` replaces it otherwise. */
    caption: DeltaCaption | null;
    tone: DeltaTone;
    message: MotivationMessage | null;
};

const CAPTIONS: Record<'current' | 'past', [DeltaCaption, DeltaCaption, DeltaCaption]> = {
    current: ['as-expected', 'ahead', 'behind'],
    past: ['target-met', 'over-target', 'under-target'],
};

/**
 * Sub-year periods accrue the daily target per weekday; a full-year view uses the annual target
 * directly and prorates it over the year's weekdays, so year-end expected equals the target.
 */
export const computeProgress = ({
    option,
    range,
    today,
    annualTargetHours,
    plannedWorkdaysOff,
    actualHours,
}: ProgressInput): Progress => {
    const day = startOfDay(today);
    const from = startOfDay(range.from);
    const to = startOfDay(range.to);
    const state: PeriodState = to < day ? 'past' : from > day ? 'future' : 'current';
    const weekdays = countWeekdays({ from, to });
    const elapsed = state === 'past' ? weekdays : state === 'future' ? 0 : countWeekdays({ from, to: day });
    const isFullYear =
        from.getFullYear() === to.getFullYear() && isSameDay(from, startOfYear(from)) && isSameDay(to, endOfYear(to));

    const dailyTarget = annualTargetHours / plannedWorkdaysPerYear(plannedWorkdaysOff, day.getFullYear());
    const periodTarget = isFullYear ? annualTargetHours : dailyTarget * weekdays;
    const expectedHours = isFullYear
        ? (annualTargetHours * elapsed) / weekdaysInYear(from.getFullYear())
        : dailyTarget * elapsed;
    const delta = actualHours - expectedHours;
    const hasTarget = periodTarget > 0;

    let bar: Progress['bar'] = null;
    if (hasTarget && state === 'future') {
        bar = { fill: 0, targetTick: 1, expectedTick: null };
    } else if (hasTarget) {
        // Over target the fill is full and the ticks move left in proportion; the scale is never compressed.
        const scale = Math.max(periodTarget, actualHours);
        bar = {
            fill: actualHours / scale,
            targetTick: periodTarget / scale,
            expectedTick: state === 'current' ? expectedHours / scale : null,
        };
    }

    let caption: DeltaCaption | null = null;
    let tone: DeltaTone = 'neutral';
    let message: MotivationMessage | null = null;
    if (state === 'future') {
        message = 'future';
    } else if (!hasTarget) {
        // No weekdays: only the hours line is shown.
    } else if (actualHours === 0 && state === 'current' && option === 'today') {
        message = 'fresh-day';
    } else if (actualHours === 0 && state === 'current' && elapsed <= 1) {
        message = 'fresh-start';
    } else {
        // The dead band is checked first, or it would only ever catch an exact zero.
        const [onPace, ahead, behind] = CAPTIONS[state];
        if (Math.abs(delta) < DEAD_BAND_HOURS) {
            caption = onPace;
        } else if (delta > 0) {
            caption = ahead;
            tone = 'positive';
        } else {
            caption = behind;
            tone = 'negative';
        }
    }

    return {
        state,
        isFullYear,
        isSingleDay: isSameDay(from, to),
        dailyTarget,
        periodTarget,
        expectedHours,
        weekdays,
        elapsed,
        delta,
        percent: hasTarget ? Math.round((actualHours / periodTarget) * 100) : null,
        bar,
        projectedTotal:
            state === 'current' && elapsed > 0 && elapsed < weekdays && actualHours > 0
                ? (actualHours * weekdays) / elapsed
                : null,
        caption,
        tone,
        message,
    };
};

/** Hours with no space before the unit and a thousands separator: `1,800h`, `8.1h`. */
export const formatHours = (hours: number, decimals: number): string =>
    `${getIntl().formatNumber(hours, { minimumFractionDigits: decimals, maximumFractionDigits: decimals })}h`;

/** Always signed, with a true minus; a value that rounds to zero keeps the plus. */
export const formatSignedHours = (hours: number, decimals: number): string => {
    const isNegative = hours < 0 && Number(Math.abs(hours).toFixed(decimals)) > 0;
    return `${isNegative ? '−' : '+'}${formatHours(Math.abs(hours), decimals)}`;
};
